// dependencies
import React, { useEffect, useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { faChevronDown, faChevronRight, faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { filterDataSets } from "./utils";

import Checkbox from "../Inputs/Checkbox";
import InputText from "../Inputs/InputText";
import css from "./ColumnPicker.module.scss";
import InfoPanel from "./Components/InfoPanel";

export interface InfoEntry {
	label: string;
	value: string | string[];
}

export interface DataValue {
	id: string;
	label: string;
	info?: InfoEntry[];
	action?: JSX.Element;
	sectionID: string;
}

export interface DataSet {
	id: string;
	label: string;
	values: DataValue[];
	action?: JSX.Element;
}

interface BaseProps {
	/**
	 * An array of data sets that represents the different sections in the column picker panel
	 */
	dataSets: DataSet[];
	/**
	 * An array of items that are checked
	 */
	checkedItems: string[];
	/**
	 * A function to handle whenever a checkbox is checked or unchecked.
	 */
	onToggleItem: (value: DataValue) => void;
	/**
	 * A function to handle whenever all checkboxes are checked or unchecked.
	 * This function is called when the "Select All" checkbox is checked or
	 * unchecked.
	 * @param allValues An array of all values in the column picker
	 * @param allColumnsChecked A boolean representing whether all columns are checked
	 * @returns void
	 */
	onToggleAllItems: (allValues: DataValue[], allColumnsChecked: boolean) => void;

	/**
	 * Optional batch handler for toggling all items in a section at once.
	 * Prefer this over onToggleItem when implementing section-level toggling,
	 * as calling onToggleItem N times requires the parent to use functional
	 * state updates to avoid stale-closure races.
	 * @param items The items to add or remove
	 * @param isDeselecting true = all items were checked (will deselect them), false = not all checked (will select the unchecked ones)
	 */
	onToggleSectionItems?: (items: DataValue[], isDeselecting: boolean) => void;
	/**
	 * An array of section ids that should be expanded by default
	 * @default []
	 */
	initialExpandedSections: string[];
}

/**
 * Column Picker allows the user to select columns to display in the grid.
 * Values within sections are searchable and section can have action buttons.
 * Information can be set in DataValues to display additional information
 * in the info panel.
 */
const ColumnPicker = (props: BaseProps) => {
	const { dataSets, checkedItems, onToggleItem, onToggleAllItems, onToggleSectionItems, initialExpandedSections } =
		props;
	// sets the initial expansion of column sections (once).
	// looks to the initialExpandedSections prop to determine if a section
	// should begin expanded
	const expanded = useMemo(() => {
		return dataSets.reduce((acc, dataSet) => {
			acc[dataSet.id] = initialExpandedSections.includes(dataSet.id) ? true : false;
			return acc;
		}, {});
	}, []);

	const [expandedSections, setExpandedSections] = useState(expanded);
	const [searchValue, setSearchValue] = useState("");
	const [sectionResults, setSectionResults] = useState<DataSet[]>([]);
	const [infoPanelInfo, setInfoPanelInfo] = useState<InfoEntry[]>([]);
	const [infoPanelOpen, setInfoPanelOpen] = useState<string | null>(null);
	const [allColumnsChecked, setAllColumnsChecked] = useState(false);

	const sectionCheckboxes = useMemo<{ [key: string]: boolean }>(() => {
		return sectionResults.reduce(
			(acc, section) => {
				acc[section.id] = section.values.length > 0 && section.values.every((v) => checkedItems.includes(v.id));
				return acc;
			},
			{} as { [key: string]: boolean }
		);
	}, [sectionResults, checkedItems]);

	const totalSearchResultsCount = sectionResults.reduce((acc, dataSet) => {
		return acc + dataSet.values.length;
	}, 0);

	useEffect(() => {
		setSectionResults(dataSets);
	}, []);

	// updates the sections list if any of the sections changes
	// Need to use deep compare because change could be add, delete or edit.
	useDeepCompareEffect(() => {
		// filter if search field updated
		if (searchValue) {
			const filteredData = filterDataSets(searchValue, dataSets);
			setSectionResults(filteredData);

			// expand the section when viewing search results
			const expandedSectionsCopy = { ...expandedSections };
			filteredData.forEach((section) => {
				expandedSectionsCopy[section.id] = true;
			});
			setExpandedSections(expandedSectionsCopy);
		} // if no search, return unfiltered search list
		else {
			setSectionResults(dataSets);
		}
	}, [dataSets, searchValue]);

	// updates the all items checked toggle box depending on if
	// the number of search results match the number of checked search results.
	useEffect(() => {
		if (totalSearchResultsCount > 0) {
			const allSearchValuesChecked = sectionResults
				.map((section) => section.values.map((value) => value))
				.flat()
				.filter((value) => checkedItems.includes(value.id));

			const searchResultsAllChecked = allSearchValuesChecked.length === totalSearchResultsCount;

			setAllColumnsChecked(searchResultsAllChecked);
		}
	}, [totalSearchResultsCount, checkedItems.length]);

	const setExpandSection = (sectionID: string) => {
		// if the section is already expanded, collapse it
		const nextState = {
			...expandedSections,
			[sectionID]: !expandedSections[sectionID],
		};
		setExpandedSections(nextState);
	};

	const handleToggleSectionItems = (sectionID: string, sectionChecked: boolean) => {
		const section = sectionResults.find((s) => s.id === sectionID);
		if (!section || !onToggleSectionItems) {
			return;
		}
		const itemsToToggle = sectionChecked
			? section.values.filter((v) => checkedItems.includes(v.id))
			: section.values.filter((v) => !checkedItems.includes(v.id));
		if (itemsToToggle.length > 0) {
			onToggleSectionItems(itemsToToggle, sectionChecked);
		}
	};
	const checkItems = (value: DataValue) => {
		// if id provided, check single item
		if (value.id) {
			onToggleItem({
				...value,
			});
		}
		// if no id provided, check all items from search results
		else {
			// return values from all sections that match search
			const allValues = sectionResults.map((section) => section.values.map((value) => value)).flat();
			setAllColumnsChecked(!allColumnsChecked);
			onToggleAllItems(allValues, allColumnsChecked);
		}
	};

	const displayInfo = (value: DataValue) => {
		if (value.info) {
			setInfoPanelOpen(value.id);
			setInfoPanelInfo(value.info);
		}
	};

	const handleCloseInfoPanel = () => {
		setInfoPanelOpen(null);
		setInfoPanelInfo([]);
	};

	const infoSectionShowing: boolean = Boolean(
		sectionResults.find((section) => {
			return (
				infoPanelOpen &&
				infoPanelInfo?.length &&
				section.values.find((col) => {
					return col.id === infoPanelOpen;
				})
			);
		})
	);
	return (
		<div className={css.columnPickerContainer}>
			<div className={css.panelHeader}>Select Columns</div>
			<div className={css.searchContainer}>
				<Checkbox value={allColumnsChecked} onChange={checkItems} />
				<div className={css.inputContainer}>
					<InputText
						className={css.searchInput}
						value={searchValue}
						onChange={(value) => setSearchValue(value)}
						placeholder="Search..."
						ariaLabel="Search columns"
					/>
					{searchValue && (
						<div className={css.clearInput} onClick={() => setSearchValue("")}>
							<FontAwesomeIcon icon={faTimes} />
						</div>
					)}
				</div>
			</div>
			<div className={css.sections}>
				{sectionResults.map((section) => {
					const showExpanded = expandedSections[section.id] === true;

					return (
						<div key={section.id} className={css.section}>
							<div className={css.labelRow}>
								<div className={css.sectionLabelContainer} data-testid={`${section.id}-expand`}>
									{onToggleSectionItems && section.values.length > 0 ? (
										<div className={css.sectionCheckbox}>
											<Checkbox
												value={sectionCheckboxes[section.id] || false}
												name={`section-${section.id}`}
												onChange={() =>
													handleToggleSectionItems(section.id, sectionCheckboxes[section.id])
												}
											/>
										</div>
									) : (
										<div className={css.sectionCheckboxDisabled}></div>
									)}
									<div
										className={css.sectionClickTarget}
										onClick={() => setExpandSection(section.id)}
									>
										<div className={css.expandSectionIcon}>
											{showExpanded ? (
												<FontAwesomeIcon icon={faChevronDown} />
											) : (
												<FontAwesomeIcon icon={faChevronRight} />
											)}
										</div>
										<div className={css.sectionLabel}>{section.label}</div>
									</div>
								</div>
								{section.action || null}
							</div>
							{showExpanded && section.values.length > 0 && (
								<div className={css.sectionValues}>
									{section.values.map((value) => {
										const isChecked = checkedItems.includes(value.id);
										const hasInfo = value.info !== undefined;
										const displayingInfo = infoPanelInfo.length && infoPanelOpen === value.id;

										return (
											<div
												key={value.id}
												className={`${css.valueWrapper} ${
													displayingInfo ? css.displayingValueInfo : ""
												}`}
											>
												<Checkbox
													className={css.valueCheckbox}
													onChange={() => checkItems(value)}
													value={isChecked}
												/>
												<div
													className={`${css.value} ${
														hasInfo && !displayingInfo ? css.hasInfo : ""
													}`}
													onClick={() => displayInfo(value)}
												>
													{value.label}
												</div>
												{value.action || null}
											</div>
										);
									})}
								</div>
							)}
						</div>
					);
				})}
			</div>
			{
				// if the column doesn't exist, this hides the info panel
				infoSectionShowing && <InfoPanel handleClose={handleCloseInfoPanel} info={infoPanelInfo} />
			}
		</div>
	);
};

export default ColumnPicker;
